import { onModalMessage } from 'src/utils/messages/modalChannel'
import { getChannelClient } from 'src/utils/messages/singleton'
import { absolutifyOptsSrc } from './openModal'

// Cross-frame dispatcher for *portal-mount* modals — modals whose
// React content lives in the donate widget's tree but renders into a
// DOM target that varies by surrounding context:
//
//   1. parent === self (standalone widget) — no parent to delegate
//      to. Caller-supplied `renderLocal({ id, ariaLabel, maxWidth,
//      scrollWithin })` opens a BS5 / TW4 modal in this same document
//      and returns `{ target, close }`. The sizing opts mirror what
//      renderModal applies on the iframe path so the picker looks
//      the same across modes.
//   2. Channel handshake succeeds (properly embedded) — send
//      `openModal` over the channel; the host frame's existing
//      onAll('openModal', …) listener runs renderModal which mounts
//      an iframe loading `/widgets/modals/blank` at host-page level.
//      The blank pack inside that iframe announces `portalReady` via
//      sendToOpener; we receive it via onModalMessage, then reach
//      into the modal iframe's body via `window.parent.frames[id]`
//      (same-origin sibling lookup — no parent.document access, so
//      partner-page origin is irrelevant) and resolve.
//   3. Channel handshake times out (improperly embedded) —
//      `renderLocal` again, same as mode 1.
//
// All three paths land on the same `Promise<{ target, close }>`
// contract; the caller does `createPortal(<Picker/>, target)` and
// invokes `close()` to dismiss when the user picks a value. The
// shadow-DOM close button on the renderModal dialog (mode 2) and the
// BS5 modal's own backdrop/ESC behaviour (mode 1/3) handle the
// other dismissal paths.
//
// Close mechanism in mode 2: the blank pack exposes a tiny
// `__closeFromOpener` function on its window. The opener calls
// `parent.frames[id].__closeFromOpener()`; because the function's
// realm is the modal iframe, its `sendToOpener('close')` flows
// through the existing modalChannel forwarder which closes the
// dialog. Keeps the opener-initiated close path on the same rails as
// the modal-initiated one.
//
// Distinct from openModal because:
//   - return semantics: Promise resolving to a DOM target + close fn
//     vs. fire-and-forget
//   - mode 1/3 fallback: a caller-supplied local renderer (donate
//     widget's BS5/TW4 markup) vs. the embed's framework-agnostic
//     renderModal iframe
//
// `scrollWithin` selects which render context gets the bounded-body
// + internal-scroll treatment (cap the modal at viewport, scroll the
// content inside instead of letting the dialog grow tall):
//   - 'local' — only modes 1/3 (the renderLocal path)
//   - 'host'  — only mode 2 (the renderModal iframe at host level)
//   - 'both'  — every mode
// Resolved to per-context booleans here so the downstream renderers
// (renderModal, the caller's renderLocal) keep a plain boolean API
// and don't need to know the string vocabulary. The names mirror the
// code paths: 'local' ↔ renderLocal, 'host' ↔ getChannelHost / the
// host-page render.
//
// See project_interactive_modal_pipeline memory ("Portal-mount
// modals") for the broader design.

export const openPortalModal = async opts => {
  // Same absolutify pass openModal does — a relative `src` would
  // otherwise resolve against the receiver's (partner page's) origin
  // and 404 once the channel send lands. The renderLocal branch
  // doesn't currently consume src, but absolutifying upfront keeps
  // future renderLocal callers honest without per-call-site
  // remembering to use the `_url` Rails helper.
  opts = absolutifyOptsSrc(opts)
  const { id, src, ariaLabel, maxWidth, scrollWithin, renderLocal } = opts

  const localScroll = scrollWithin === 'local' || scrollWithin === 'both'
  const hostScroll = scrollWithin === 'host' || scrollWithin === 'both'

  // Clip the local modal-content to its rounded corners whenever
  // scrollWithin was requested in *any* context. Opting into
  // scrollWithin signals the content is meant to be bounded, so
  // clipping is safe — and a 'host'-only modal (e.g. the donor feed)
  // still wants clean corners in local mode even though it doesn't
  // scroll internally there. Modals that never set scrollWithin stay
  // unclipped, preserving the escape hatch for a future modal with an
  // intentionally-overflowing dropdown / popover. (localScroll alone
  // already clips via BS5's modal-dialog-scrollable; this flag covers
  // the host-only gap and lets the renderer clip without depending on
  // that BS5 internal.)
  const clipContent = localScroll || hostScroll
  const localOpts = { id, ariaLabel, maxWidth, scrollWithin: localScroll, clipContent }

  if (parent === self) return renderLocal(localOpts)

  const client = getChannelClient()
  try {
    await client.ready
  } catch {
    return renderLocal(localOpts)
  }

  return new Promise(resolve => {
    // Subscribe BEFORE sending so a fast iframe-load + portalReady
    // can't race past us. onModalMessage de-dupes any filter-mismatch
    // fires — `payload.id !== id` returns without resolving.
    const unsubscribe = onModalMessage('portalReady', payload => {
      if (payload?.id !== id) return
      unsubscribe()
      const modalWindow = parent.frames[id]
      const doc = modalWindow?.document

      // scrollWithin modals pair the body/iframe max-height cap (set
      // via data-scroll-within in dialogStyles) with this overflow
      // override on the iframe document, so the document scrolls
      // internally beneath a sticky picker header instead of being
      // clipped by resizeIframe's inline `documentElement.style
      // .overflow = 'hidden'`. The !important rule beats that inline
      // style per cascade. Other portal-mount modals keep
      // resizeIframe's default growth behaviour and don't need this.
      if (hostScroll && doc) {
        const overflowOverride = doc.createElement('style')
        overflowOverride.textContent = 'html { overflow: auto !important; }'
        doc.head.appendChild(overflowOverride)
      }

      resolve({
        // Cross-origin .document access would throw; both donate
        // widget and modal iframe are pledge.to so this works. The
        // `?.` guards against a stale unmount race where the iframe
        // is gone by the time we resolve.
        target: doc?.body ?? null,
        // No-op when the modal iframe has unmounted; otherwise runs
        // in the modal iframe's realm and triggers the host-side
        // dialog close via the existing modalChannel forwarder.
        close: () => modalWindow?.__closeFromOpener?.(),
      })
    })

    client.send('openModal', { id, src, ariaLabel, maxWidth, scrollWithin: hostScroll })
  })
}
